// [claude-code 2026-05-27] Desktop runtime API-base resolver.
// Electron injects the active backend URL at runtime; it must beat Vite's
// build-time VITE_API_URL so packaged Desktop can use local or Portless routes.

type RuntimeWindow = Window & {
  __FINTHEON_API_BASE__?: string;
  __FINTHEON_FETCH_BRIDGE_INSTALLED__?: boolean;
  __FINTHEON_EVENTSOURCE_BRIDGE_INSTALLED__?: boolean;
  electron?: {
    apiBase?: string;
    apiSource?: string;
  };
};

const LOCAL_API_BASES = ["http://localhost:8080", "http://127.0.0.1:8080"];
const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/i;

export function getRuntimeApiBase(fallback = "http://localhost:8080"): string {
  const runtimeBase = readRuntimeApiBase();
  if (runtimeBase) return runtimeBase;

  if (isRemoteBrowserHost()) return "";

  const envBase = import.meta.env.VITE_API_URL;
  if (envBase) return stripTrailingSlash(envBase);

  return fallback;
}

export function installRuntimeApiBaseFetchBridge(): void {
  if (typeof window === "undefined") return;

  const runtimeWindow = window as RuntimeWindow;
  const runtimeBase = readRuntimeApiBase();
  const targetBase = runtimeBase ?? getRemoteBrowserRewriteBase();
  if (targetBase === null) return;

  const rewriteOrigins = buildRewriteOrigins(targetBase, runtimeBase);
  if (rewriteOrigins.size === 0) return;

  installFetchBridge(targetBase, rewriteOrigins, runtimeWindow);
  installEventSourceBridge(targetBase, rewriteOrigins, runtimeWindow);
}

function installFetchBridge(
  targetBase: string,
  rewriteOrigins: Set<string>,
  runtimeWindow: RuntimeWindow,
): void {
  if (runtimeWindow.__FINTHEON_FETCH_BRIDGE_INSTALLED__) return;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    return originalFetch(
      rewriteFetchInput(input, targetBase, rewriteOrigins),
      init,
    );
  };
  runtimeWindow.__FINTHEON_FETCH_BRIDGE_INSTALLED__ = true;
}

function readRuntimeApiBase(): string | null {
  if (typeof window === "undefined") return null;

  const runtimeWindow = window as RuntimeWindow;
  const electronBase = runtimeWindow.electron?.apiBase;
  const raw = runtimeWindow.__FINTHEON_API_BASE__ || electronBase;
  if (!raw) return null;

  return stripTrailingSlash(raw);
}

function installEventSourceBridge(
  targetBase: string,
  rewriteOrigins: Set<string>,
  runtimeWindow: RuntimeWindow,
): void {
  if (runtimeWindow.__FINTHEON_EVENTSOURCE_BRIDGE_INSTALLED__) return;
  if (typeof window.EventSource === "undefined") return;

  const NativeEventSource = window.EventSource;
  const FintheonEventSource = function (
    this: EventSource,
    url: string | URL,
    eventSourceInitDict?: EventSourceInit,
  ) {
    const rewritten = rewriteUrl(url, targetBase, rewriteOrigins);
    return new NativeEventSource(rewritten ?? url, eventSourceInitDict);
  } as unknown as typeof EventSource;

  FintheonEventSource.prototype = NativeEventSource.prototype;
  Object.defineProperties(FintheonEventSource, {
    CONNECTING: { value: NativeEventSource.CONNECTING },
    OPEN: { value: NativeEventSource.OPEN },
    CLOSED: { value: NativeEventSource.CLOSED },
  });

  window.EventSource = FintheonEventSource;
  runtimeWindow.__FINTHEON_EVENTSOURCE_BRIDGE_INSTALLED__ = true;
}

function buildRewriteOrigins(
  targetBase: string,
  runtimeBase: string | null,
): Set<string> {
  const targetOrigin = toOrigin(targetBase);
  const origins = new Set<string>();

  const envOrigin = toOrigin(import.meta.env.VITE_API_URL);
  if (envOrigin && envOrigin !== targetOrigin) origins.add(envOrigin);

  for (const base of LOCAL_API_BASES) {
    const origin = toOrigin(base);
    if (origin && origin !== targetOrigin) origins.add(origin);
  }

  if (runtimeBase) return origins;
  if (isRemoteBrowserHost()) return origins;
  return origins;
}

function rewriteFetchInput(
  input: RequestInfo | URL,
  runtimeBase: string,
  rewriteOrigins: Set<string>,
): RequestInfo | URL {
  const rewritten = rewriteUrl(input, runtimeBase, rewriteOrigins);
  if (!rewritten) return input;

  if (typeof input === "string") return rewritten;
  if (input instanceof URL) return rewritten;
  if (input instanceof Request) {
    return new Request(rewritten, {
      body: input.body,
      cache: input.cache,
      credentials: input.credentials,
      headers: input.headers,
      integrity: input.integrity,
      keepalive: input.keepalive,
      method: input.method,
      mode: input.mode,
      redirect: input.redirect,
      referrer: input.referrer,
      referrerPolicy: input.referrerPolicy,
      signal: input.signal,
    });
  }

  return input;
}

function rewriteUrl(
  input: RequestInfo | URL,
  runtimeBase: string,
  rewriteOrigins: Set<string>,
): string | null {
  const raw =
    typeof input === "string" || input instanceof URL
      ? String(input)
      : input.url;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (!rewriteOrigins.has(url.origin)) return null;

  if (!runtimeBase) return `${url.pathname}${url.search}${url.hash}`;

  const target = new URL(runtimeBase);
  target.pathname = url.pathname;
  target.search = url.search;
  target.hash = url.hash;
  return target.toString();
}

function getRemoteBrowserRewriteBase(): string | null {
  if (!isRemoteBrowserHost()) return null;
  return "";
}

function isRemoteBrowserHost(): boolean {
  if (typeof window === "undefined") return false;
  if (!/^https?:$/.test(window.location.protocol)) return false;
  return !LOCAL_HOST_PATTERN.test(window.location.hostname);
}

function toOrigin(value: string | undefined): string | null {
  if (!value) return null;
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/$/, "");
}
